import React, { useMemo } from 'react';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

import {
  IBaseDrawerProps,
  ModifyPlanSpecificTaxationFormValues
} from '../types';

import PrecisionService from 'src/core/services/PrecisionService';
import {
  useGetCurrencies,
  useGetTaxationPlanSpecificById,
  useModifyTaxationPlanSpecific
} from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';
import { modifyPlanSpecificTaxationDrawerSections } from '@common/constants';

const toDisplayPercent = (v: number | null | undefined): string => {
  if (v == null) return '';
  const p = v * 100;
  return p % 1 === 0 ? String(p) : String(Number(p.toFixed(2)));
};

const stripPercent = (v: any) =>
  typeof v === 'string' ? v.replace('%', '').trim() : v;
interface Props extends IBaseDrawerProps {
  taxationPlanSpecificId: string;
}
const ModifyPlanSpecificTaxationDrawer: React.FC<Props> = ({
  taxationPlanSpecificId,
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data: currenciesResp } = useGetCurrencies.useQuery();

  const { data: entityResp } = useGetTaxationPlanSpecificById.useQuery({
    options: { variables: { taxationPlanSpecificId } }
  });

  const [modifyPlanSpecific] = useModifyTaxationPlanSpecific();

  const entity = entityResp?.core?.getTaxationPlanSpecificById ?? null;

  const currencyOptions = useMemo<Record<string, string>>(() => {
    const list = currenciesResp?.core?.getCurrencies ?? [];
    return list.reduce((acc: Record<string, string>, c: any) => {
      acc[c.id] = c.title;
      return acc;
    }, {});
  }, [currenciesResp]);

  const lineOptions = useMemo<Record<string, string>>(() => {
    if (!entity?.line?.id) return {};
    return { [entity.line.id]: `${entity.line.code} - ${entity.line.name}` };
  }, [entity]);

  const planOptions = useMemo<Record<string, string>>(() => {
    if (!entity?.plan?.id) return {};
    return { [entity.plan.id]: `${entity.plan.code} - ${entity.plan.name}` };
  }, [entity]);

  const initialValues = useMemo<ModifyPlanSpecificTaxationFormValues>(() => {
    return {
      lineID: entity?.lineID ? [entity.lineID] : [],
      planID: entity?.planID ? [entity.planID] : [],
      fixedStamp: entity?.fixedStamp != null ? String(entity.fixedStamp) : '',
      fixedStampCurrency: entity?.fixedStampCurrency
        ? [entity.fixedStampCurrency]
        : [],
      proportionalStamp: toDisplayPercent(entity?.proportionalStamp),
      municipalityTax: toDisplayPercent(entity?.municipalityTax),
      taxOnCommission: toDisplayPercent(entity?.taxOnCommission),
      reinsuranceTax: toDisplayPercent(entity?.reinsuranceTax)
    };
  }, [entity]);

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    return modifyPlanSpecificTaxationDrawerSections(
      initialValues,
      lineOptions,
      planOptions,
      currencyOptions
    );
  }, [initialValues, lineOptions, planOptions, currencyOptions]);

  const submitForm = async (rawValues: Record<string, any>) => {
    const [values] = normalizeDynamicFormDrawerValues(sections, rawValues);
    try {
      const input = {
        id: taxationPlanSpecificId,
        lineID: Array.isArray(values.lineID) ? values.lineID[0] : values.lineID,
        planID: Array.isArray(values.planID) ? values.planID[0] : values.planID,
        fixedStamp: values.fixedStamp ? parseFloat(values.fixedStamp) : null,
        fixedStampCurrency: Array.isArray(values.fixedStampCurrency)
          ? values.fixedStampCurrency[0]
          : values.fixedStampCurrency,
        proportionalStamp: PrecisionService.divideBy100(
          stripPercent(values.proportionalStamp)
        ),
        municipalityTax: PrecisionService.divideBy100(
          stripPercent(values.municipalityTax)
        ),
        taxOnCommission: PrecisionService.divideBy100(
          stripPercent(values.taxOnCommission)
        ),
        reinsuranceTax: PrecisionService.divideBy100(
          stripPercent(values.reinsuranceTax)
        )
      };

      const res = await modifyPlanSpecific({
        planSpecificTaxationInput: input
      });

      if (!res?.errors?.length) {
        onSuccess?.();
      }
    } catch (err) {
      <ToastErrorMessage>
        {getError(err) || DEFAULT_ERROR_TEXT}
      </ToastErrorMessage>;
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
    />
  );
};

export { ModifyPlanSpecificTaxationDrawer };
